import { useState, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminGetStripePriceEntitlements,
  useAdminDeleteStripePriceEntitlement,
  getAdminGetStripePriceEntitlementsQueryKey,
} from '@/api/auth/admin/admin';
import type { AdminGetStripePriceEntitlementsQueryResult } from '@/api/auth/admin/admin';
import type { StripePriceEntitlementResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Plus, Trash2, ChevronRight, Package, Pencil } from 'lucide-react';
import { AddStripePriceDialog } from './AddStripePriceDialog';
import { EditStripePriceDialog } from './EditStripePriceDialog';
import type { EntitlementRule } from '@/api/auth/missionImpactAuthAPI.schemas';
import { toast } from 'sonner';
import { logger } from '@/services/logger';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { type BillingScenario } from '@/lib/billing-scenarios';
import { ScenarioCoverage } from './ScenarioCoverage';

/**
 * Typed Stripe price entitlement with properly parsed entitlement rule
 */
interface TypedStripePriceEntitlement extends Omit<StripePriceEntitlementResponse, 'entitlementRule'> {
  entitlementRule?: EntitlementRule;
  basePriceId?: string;
}

/**
 * Base price with its add-ons grouped together
 */
interface PriceWithAddOns {
  basePrice: TypedStripePriceEntitlement;
  addOns: TypedStripePriceEntitlement[];
}

/**
 * Props for the StripePricesForm component
 */
interface StripePricesFormProps {
  /** The product ID */
  productId: string;
}

/**
 * Component for managing Stripe price mappings for a product
 */
export function StripePricesForm({ productId }: StripePricesFormProps) {
  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [addOnDialogState, setAddOnDialogState] = useState<{
    isOpen: boolean;
    basePriceId: string | null;
    stripeProductId: string | null;
  }>({
    isOpen: false,
    basePriceId: null,
    stripeProductId: null,
  });
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [deleteDialogState, setDeleteDialogState] = useState<{
    isOpen: boolean;
    stripePriceId: string | null;
  }>({
    isOpen: false,
    stripePriceId: null,
  });
  const [editDialogState, setEditDialogState] = useState<{
    isOpen: boolean;
    price: TypedStripePriceEntitlement | null;
  }>({
    isOpen: false,
    price: null,
  });

  // Memoize the selector function for this product's prices
  const selectTypedPrices = useCallback(
    (data: AdminGetStripePriceEntitlementsQueryResult): TypedStripePriceEntitlement[] => {
      // The API returns a typed, polymorphic EntitlementRule directly.
      return (data?.data?.stripePriceEntitlements ?? []) as TypedStripePriceEntitlement[];
    },
    []
  );

  // Fetch all Stripe price entitlements for this product
  const {
    data: stripePrices,
    isLoading,
    error,
  } = useAdminGetStripePriceEntitlements(productId, {
    query: {
      select: selectTypedPrices,
    },
  });

  // Group base prices with their add-ons
  const pricesWithAddOns = useMemo<PriceWithAddOns[]>(() => {
    if (!stripePrices) return [];

    // Separate base prices (no basePriceId) from add-ons (has basePriceId)
    const basePrices = stripePrices.filter((p) => !p.basePriceId);
    const addOns = stripePrices.filter((p) => !!p.basePriceId);

    return basePrices.map((basePrice) => ({
      basePrice,
      addOns: addOns.filter((addOn) => addOn.basePriceId === basePrice.stripePriceId),
    }));
  }, [stripePrices]);

  // Distinct scenarios configured across this product's prices (drives the coverage panel).
  const configuredScenarios = useMemo<BillingScenario[]>(() => {
    const set = new Set<BillingScenario>();
    for (const price of stripePrices ?? []) {
      if (price.scenario) {
        set.add(price.scenario);
      }
    }
    return Array.from(set);
  }, [stripePrices]);

  const { mutate: deleteStripePriceEntitlement, isPending: isDeleting } = useAdminDeleteStripePriceEntitlement({
    mutation: {
      onSuccess: () => {
        toast.success('Stripe price mapping deleted successfully');
        queryClient.invalidateQueries({
          queryKey: getAdminGetStripePriceEntitlementsQueryKey(productId),
        });
        setDeleteDialogState({ isOpen: false, stripePriceId: null });
      },
      onError: (error: any) => {
        logger.error(error, {
          context: 'DeleteStripePriceEntitlement',
          stripePriceId: deleteDialogState.stripePriceId,
        });
        toast.error(
          'Failed to delete Stripe price mapping. ' +
            (error.response?.data?.detail || error.message)
        );
      },
    },
  });

  /**
   * Handle opening the delete dialog
   */
  const handleDeleteClick = useCallback((stripePriceId: string) => {
    setDeleteDialogState({
      isOpen: true,
      stripePriceId,
    });
  }, []);

  /**
   * Handle opening the edit dialog
   */
  const handleEditClick = useCallback((price: TypedStripePriceEntitlement) => {
    setEditDialogState({
      isOpen: true,
      price,
    });
  }, []);

  /**
   * Handle closing the edit dialog
   */
  const handleEditDialogClose = useCallback((open: boolean) => {
    if (!open) {
      setEditDialogState({ isOpen: false, price: null });
    }
  }, []);

  /**
   * Handle opening the add-on dialog
   */
  const handleAddAddOnClick = useCallback((basePriceId: string, stripeProductId: string) => {
    setAddOnDialogState({
      isOpen: true,
      basePriceId,
      stripeProductId,
    });
  }, []);

  /**
   * Handle closing the add-on dialog
   */
  const handleAddOnDialogClose = useCallback((open: boolean) => {
    if (!open) {
      setAddOnDialogState({ isOpen: false, basePriceId: null, stripeProductId: null });
    }
  }, []);

  /**
   * Toggle row expansion
   */
  const toggleRowExpansion = useCallback((stripePriceId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(stripePriceId)) {
        next.delete(stripePriceId);
      } else {
        next.add(stripePriceId);
      }
      return next;
    });
  }, []);

  /**
   * Handle confirming the delete
   */
  const handleDeleteConfirm = useCallback(() => {
    if (deleteDialogState.stripePriceId) {
      deleteStripePriceEntitlement({ productId, stripePriceId: deleteDialogState.stripePriceId });
    }
  }, [deleteDialogState.stripePriceId, deleteStripePriceEntitlement, productId]);

  /**
   * Format rule for display
   */
  const formatRule = useCallback((rule?: EntitlementRule): string => {
    if (!rule) return 'N/A';
    if ('type' in rule && rule.type === 'cumulative') {
      return `Cumulative: ${rule.count}`;
    }
    if ('type' in rule && rule.type === 'minimum') {
      return `Minimum: ${rule.minimum}`;
    }
    return 'Unknown';
  }, []);

  /**
   * Render the scenario cell: the scenario badge plus a tier-default marker.
   */
  const renderScenarioCell = useCallback((price: TypedStripePriceEntitlement) => {
    if (!price.scenario) {
      return <span className="text-muted-foreground">&#x2014;</span>;
    }
    return (
      <div className="flex flex-col items-start gap-1">
        <Badge variant="outline">{price.scenario}</Badge>
        {price.isTierDefault && (
          <Badge variant="secondary" className="text-xs">
            tier default
          </Badge>
        )}
      </div>
    );
  }, []);

  // Handle loading state
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-8 w-[200px]" />
          <Skeleton className="h-4 w-[300px]" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-10 w-[150px]" />
            <Skeleton className="h-[200px] w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Stripe Prices</CardTitle>
          <CardDescription>Failed to load Stripe prices</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center text-destructive py-8">
            <p>Failed to load Stripe prices: {(error as any)?.message || 'Unknown error'}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Stripe Prices</CardTitle>
            <CardDescription>
              Configure Stripe price mappings for this product. Each price defines what entitlement
              customers receive when they purchase it.
            </CardDescription>
          </div>
          <Button onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Price
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {pricesWithAddOns.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No Stripe prices configured yet. Add a price to enable purchases for this product.</p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]"></TableHead>
                  <TableHead>Stripe Price ID</TableHead>
                  <TableHead>Stripe Product ID</TableHead>
                  <TableHead>Scenario</TableHead>
                  <TableHead>Entitlement Rule</TableHead>
                  <TableHead className="w-[140px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pricesWithAddOns.map(({ basePrice, addOns }) => {
                  const isExpanded = expandedRows.has(basePrice.stripePriceId!);
                  const hasAddOns = addOns.length > 0;

                  return (
                    <Collapsible key={basePrice.stripePriceId} asChild open={isExpanded}>
                      <>
                        <TableRow className="group">
                          <TableCell>
                            {hasAddOns ? (
                              <CollapsibleTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleRowExpansion(basePrice.stripePriceId!)}
                                >
                                  <ChevronRight
                                    className={cn(
                                      'h-4 w-4 transition-transform',
                                      isExpanded && 'rotate-90'
                                    )}
                                  />
                                </Button>
                              </CollapsibleTrigger>
                            ) : (
                              <div className="w-8" />
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem]">
                              {basePrice.stripePriceId}
                            </code>
                            {hasAddOns && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                ({addOns.length} add-on{addOns.length > 1 ? 's' : ''})
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem]">
                              {basePrice.stripeProductId}
                            </code>
                          </TableCell>
                          <TableCell>{renderScenarioCell(basePrice)}</TableCell>
                          <TableCell>{formatRule(basePrice.entitlementRule)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditClick(basePrice)}
                                title="Edit price"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  handleAddAddOnClick(
                                    basePrice.stripePriceId!,
                                    basePrice.stripeProductId!
                                  )
                                }
                                title="Add add-on price"
                              >
                                <Package className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteClick(basePrice.stripePriceId!)}
                                title="Delete price"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>

                        <CollapsibleContent asChild>
                          <>
                            {addOns.map((addOn) => (
                              <TableRow key={addOn.stripePriceId} className="bg-muted/30">
                                <TableCell></TableCell>
                                <TableCell className="font-mono text-sm">
                                  <div className="flex items-center gap-2 pl-4">
                                    <span className="text-muted-foreground">&#x2514;</span>
                                    <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem]">
                                      {addOn.stripePriceId}
                                    </code>
                                    <span className="text-xs text-muted-foreground">(add-on)</span>
                                  </div>
                                </TableCell>
                                <TableCell className="font-mono text-sm">
                                  <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem]">
                                    {addOn.stripeProductId}
                                  </code>
                                </TableCell>
                                <TableCell>{renderScenarioCell(addOn)}</TableCell>
                                <TableCell>{formatRule(addOn.entitlementRule)}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleEditClick(addOn)}
                                      title="Edit add-on"
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteClick(addOn.stripePriceId!)}
                                      title="Delete add-on"
                                    >
                                      <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </>
                        </CollapsibleContent>
                      </>
                    </Collapsible>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <ScenarioCoverage configuredScenarios={configuredScenarios} className="mt-6" />
      </CardContent>

      <AddStripePriceDialog
        productId={productId}
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
      />

      {addOnDialogState.basePriceId && addOnDialogState.stripeProductId && (
        <AddStripePriceDialog
          productId={productId}
          open={addOnDialogState.isOpen}
          onOpenChange={handleAddOnDialogClose}
          basePriceId={addOnDialogState.basePriceId}
          stripeProductId={addOnDialogState.stripeProductId}
        />
      )}

      <AlertDialog
        open={deleteDialogState.isOpen}
        onOpenChange={(open) => setDeleteDialogState((prev) => ({ ...prev, isOpen: open }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Stripe Price Mapping</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this Stripe price mapping? This will not affect
              existing subscriptions, but new purchases of this price will no longer grant
              entitlements.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {editDialogState.price && editDialogState.price.entitlementRule && (
        <EditStripePriceDialog
          productId={productId}
          stripePriceId={editDialogState.price.stripePriceId!}
          open={editDialogState.isOpen}
          onOpenChange={handleEditDialogClose}
          entitlementRule={editDialogState.price.entitlementRule}
          adjustableQuantity={editDialogState.price.adjustableQuantity ?? false}
          minQuantity={editDialogState.price.minQuantity ?? 0}
          maxQuantity={editDialogState.price.maxQuantity}
          scenario={editDialogState.price.scenario ?? null}
          isTierDefault={editDialogState.price.isTierDefault ?? false}
        />
      )}
    </Card>
  );
}
